import {
  EXPORT_REGISTRY,
  projectExportConfigSchema,
  type ExportDestination,
  type ProjectExportConfig,
} from "@iamu/shared";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { AgentWorkLeasesTable } from "./agent-work-leases-table";

interface ExportConfigCardProps {
  config: ProjectExportConfig;
  onChange: (config: ProjectExportConfig) => void;
  hasLinkedRepo?: boolean;
  projectId?: string;
  /** Render without Card wrapper (for embedding in a parent page) */
  bare?: boolean;
}

export function ExportConfigCard({
  config,
  onChange,
  hasLinkedRepo,
  projectId,
  bare,
}: ExportConfigCardProps) {
  const enabledTools = config.enabledTools ?? [];

  function toggleTool(toolId: ExportDestination, enabled: boolean) {
    const updated = enabled
      ? [...new Set([...enabledTools, toolId])]
      : enabledTools.filter((t) => t !== toolId);
    onChange({ ...config, enabledTools: updated });
  }

  const toolsSection =
    EXPORT_REGISTRY.length > 0 ? (
      <div className="divide-y divide-border border-t border-border/60 pt-4">
        {EXPORT_REGISTRY.map((tool) => {
          const isEnabled = enabledTools.includes(tool.id);
          const isAvailable = tool.enabled;
          const needsRepo = tool.requiresLinkedRepo && hasLinkedRepo === false;

          return (
            <div key={tool.id} className="py-4 first:pt-0 last:pb-0">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Label
                      htmlFor={`export-${tool.id}`}
                      className={
                        isAvailable
                          ? "text-sm font-medium"
                          : "text-sm text-muted-foreground"
                      }
                    >
                      {tool.name}
                    </Label>
                    {!isAvailable && (
                      <span className="text-xs text-muted-foreground">
                        Coming soon
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {tool.description}
                    {needsRepo && <span className="ml-1">(not connected)</span>}
                  </p>
                </div>
                <Switch
                  id={`export-${tool.id}`}
                  checked={isEnabled}
                  onCheckedChange={(checked) => toggleTool(tool.id, checked)}
                  disabled={!isAvailable}
                  aria-label={`Enable ${tool.name} auto-fix`}
                />
              </div>
            </div>
          );
        })}
      </div>
    ) : null;

  const content = (
    <div className="space-y-6">
      <AgentWorkLeasesTable projectId={projectId} />
      {toolsSection}
    </div>
  );

  if (bare) return content;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">
          Active agent work leases
        </CardTitle>
        <CardDescription className="text-xs">
          Live queue of claimed 2-hour exclusive agent locks and pending work
          requests across workspace projects. Workspace owners and editors can
          force-release stuck locks when an agent session disconnects.
        </CardDescription>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}

export function ExportsTab() {
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.tenant.getDefaultExportConfig.useQuery();

  const mutation = trpc.tenant.setDefaultExportConfig.useMutation({
    onSuccess: (result) => {
      utils.tenant.getDefaultExportConfig.setData(undefined, result);
      toast.success("Default export configuration saved");
    },
    onError: (err) =>
      toastTrpcError(err, "Failed to save export configuration"),
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-6">
          <Skeleton className="h-48" />
        </CardContent>
      </Card>
    );
  }

  const config = projectExportConfigSchema.parse(data ?? {});

  return (
    <div className="space-y-4">
      <ExportConfigCard
        config={config}
        onChange={(updated) => mutation.mutate(updated)}
      />
    </div>
  );
}
